import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Package, CheckCircle2, Calendar } from "lucide-react";
import { useSEO, useJsonLd } from "../hooks/useSEO";

const IMG_RHIZOMES = "/images/Rhizome-1.webp";
const IMG_PAILLE = "/images/Paille-vrac.jpeg";
const IMG_BIGBAG = "/images/Sachet-3.webp";

const SITE = "https://www.miscanthus-sarthe.fr";

const PRODUCTS_JSONLD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Product",
      name: "Rhizomes de Miscanthus giganteus",
      description: "Big bag de 10 000 rhizomes frais couvrant 5 000 m² (2 big bags par hectare). Taux de reprise supérieur à 80 %. Planteuse mise à disposition, conseils techniques inclus. Disponibles de février à fin avril.",
      image: `${SITE}/images/Rhizome-1.webp`,
      brand: { "@type": "Brand", name: "EARL Torcé Biomasse Energie" },
      offers: {
        "@type": "Offer",
        url: `${SITE}/produits`,
        price: "1500",
        priceCurrency: "EUR",
        availability: "https://schema.org/InStock",
        seller: { "@id": `${SITE}/#business` },
      },
    },
    {
      "@type": "Product",
      name: "Paille de miscanthus en vrac",
      description: "Paille de miscanthus en vrac pour paillage de grandes surfaces, litière animale et combustible biomasse. Produite en Sarthe, disponible toute l'année selon stock.",
      image: `${SITE}/images/Paille-vrac.jpeg`,
      brand: { "@type": "Brand", name: "EARL Torcé Biomasse Energie" },
      offers: {
        "@type": "Offer",
        url: `${SITE}/produits`,
        priceSpecification: {
          "@type": "UnitPriceSpecification",
          price: "24",
          priceCurrency: "EUR",
          unitText: "m³",
        },
        availability: "https://schema.org/InStock",
        seller: { "@id": `${SITE}/#business` },
      },
    },
    {
      "@type": "Product",
      name: "Sachet de paille de miscanthus 120 L",
      description: "Sachet de 120 litres de paille de miscanthus, couvre 3 à 5 m² de paillage. Format pratique pour particuliers et petites surfaces. Remise par palette de 50 sachets et plus.",
      image: `${SITE}/images/Sachet-3.webp`,
      brand: { "@type": "Brand", name: "EARL Torcé Biomasse Energie" },
      offers: {
        "@type": "Offer",
        url: `${SITE}/produits`,
        price: "15",
        priceCurrency: "EUR",
        availability: "https://schema.org/InStock",
        seller: { "@id": `${SITE}/#business` },
      },
    },
  ],
};

const products = [
  {
    id: "rhizomes",
    name: "Rhizomes de Miscanthus",
    availability: "Février à fin Avril",
    img: IMG_RHIZOMES,
    badge: "Saison 2025",
    color: "#2d5016",
    desc: "Rhizomes frais sélectionnés sur notre exploitation pour un taux de reprise optimal. Une planteuse à pomme de terre est mise à disposition pour faciliter la mise en place.",
    features: [
      "10 000 rhizomes par big bag",
      "1 big bag couvre 5 000 m² — 2 big bags par hectare",
      "Sélectionnés sur notre exploitation",
      "Taux de reprise > 80%",
      "Planteuse à pomme de terre mise à disposition",
      "Conseils techniques inclus",
      "Livraison possible",
    ],
    note: "Commander avant fin janvier pour garantir la disponibilité.",
    months: ["Février", "Mars", "Avril"],
    href: "/les-rhizomes",
  },
  {
    id: "paille-vrac",
    name: "Paille de Miscanthus en vrac",
    availability: "Toute l'année",
    img: IMG_PAILLE,
    badge: "Disponible",
    color: "#b57a3e",
    desc: "Paille en vrac pour paillage de grandes surfaces, jardins paysagers, potager et litière animale en grande quantité. Livraison possible.",
    features: [
      "Idéal pour grandes surfaces (> 100 m²)",
      "Excellent pour paillage de jardins",
      "Parfait comme litière pour volailles et bovins",
      "Livraison en vrac sur demande",
    ],
    note: "Livraison possible toute l'année selon les stocks disponibles.",
    months: ["Toute l'année"],
    href: "/la-paille",
  },
  {
    id: "sachet-120l",
    name: "Sachet de Paille 120L",
    availability: "Toute l'année",
    img: IMG_BIGBAG,
    badge: "Disponible",
    color: "#b57a3e",
    desc: "Format pratique en sachet de 120 litres pour particuliers, paysagistes et petites surfaces. Facile à manipuler et à transporter.",
    features: [
      "Contenance : 120 litres / sachet",
      "Couvre environ 3 à 5 m²",
      "Facile à manipuler",
      "Parfait pour jardins de ville",
      "Retrait possible sur place ou livraison",
    ],
    note: "Remise possible pour achat en palette (50 sachets et +).",
    months: ["Toute l'année"],
    href: "/la-paille",
  },
];

export default function Produits() {
  useSEO({
    title: "Rhizomes et paille de miscanthus à la vente | Miscanthus Sarthe",
    description: "Rhizomes en big bag (février-avril), paille en vrac et sachets 120L toute l'année. Produits sur notre exploitation en Sarthe, livraison Pays de la Loire.",
    path: "/produits",
  });
  useJsonLd("jsonld-produits", PRODUCTS_JSONLD);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="produits-page" style={{ backgroundColor: "#f5f2ec" }}>
      {/* Header */}
      <div className="page-header" style={{ paddingTop: "7rem" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="section-divider mb-4" />
          <h1 className="font-display text-5xl sm:text-6xl mb-5" style={{ color: "#3a2b24" }}>Nos Produits</h1>
          <p className="text-base max-w-2xl" style={{ color: "#3a2b24", opacity: 0.7, fontFamily: "'Satoshi', sans-serif" }}>
            Rhizomes de miscanthus, paille en vrac et sachets 120L. Tous les produits sont issus de notre exploitation en Sarthe.
          </p>
        </div>
      </div>

      {/* Products list */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-12">
          {products.map((p, i) => (
            <div
              key={p.id}
              id={p.id}
              className="rounded-2xl overflow-hidden grid grid-cols-1 lg:grid-cols-2"
              style={{ border: "1px solid #d8d1c4", backgroundColor: "white" }}
              data-testid={`product-${p.id}`}
            >
              <div className={`${i % 2 === 1 ? "lg:order-2" : ""} relative`}>
                <img src={p.img} alt={p.name} loading="lazy" decoding="async" className="w-full h-full object-cover" style={{ minHeight: "320px", maxHeight: "480px" }} />
                <span
                  className="absolute top-4 left-4 px-3 py-1 text-xs font-body font-500 rounded-full"
                  style={{ backgroundColor: p.color, color: "white" }}
                >
                  {p.badge}
                </span>
              </div>
              <div className={`p-8 lg:p-12 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                <div className="flex items-start justify-between mb-2">
                  <h2 className="font-display text-3xl" style={{ color: "#3a2b24" }}>{p.name}</h2>
                </div>
                <div className="flex items-center gap-2 mb-3 px-4 py-2 rounded-md inline-flex" style={{ backgroundColor: "#eae4d8" }}>
                  <span className="text-sm font-body font-500" style={{ color: "#2d5016", fontFamily: "'Satoshi', sans-serif" }}>
                    Tarifs sur devis — contactez-nous pour obtenir votre tarif personnalisé
                  </span>
                </div>
                <div className="flex items-center gap-2 mb-6">
                  <Calendar size={14} style={{ color: "#b57a3e" }} />
                  <span className="text-sm" style={{ color: "#b57a3e", fontFamily: "'Satoshi', sans-serif", fontWeight: 500 }}>{p.availability}</span>
                </div>
                <p className="text-sm leading-relaxed mb-6" style={{ color: "#3a2b24", opacity: 0.75, fontFamily: "'Satoshi', sans-serif" }}>{p.desc}</p>
                <ul className="space-y-2 mb-6">
                  {p.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm" style={{ color: "#3a2b24", fontFamily: "'Satoshi', sans-serif" }}>
                      <CheckCircle2 size={15} style={{ color: "#2d5016", marginTop: "2px", flexShrink: 0 }} />
                      {f}
                    </li>
                  ))}
                </ul>
                <p className="text-xs px-3 py-2 rounded-md mb-6 inline-block" style={{ backgroundColor: "#eae4d8", color: "#b57a3e", fontFamily: "'Satoshi', sans-serif" }}>
                  {p.note}
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to="/contact" className="btn-primary px-6 py-3 rounded-md font-body font-500 text-sm inline-flex items-center justify-center gap-2" data-testid={`product-cta-${p.id}`}>
                    Commander / Devis <ArrowRight size={16} />
                  </Link>
                  <Link to={p.href} className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md text-sm font-body font-500" style={{ color: "#2d5016", border: "1px solid #2d5016" }}>
                    En savoir plus
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Info livraison */}
      <section style={{ backgroundColor: "#eae4d8" }} className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Package, title: "Livraison Sarthe & PDL", desc: "Livraison possible en Sarthe et dans les départements voisins des Pays de la Loire. Tarif selon distance et volume." },
              { icon: CheckCircle2, title: "Retrait sur place", desc: "Retrait possible directement à l'exploitation à Torcé en Vallée, Sarthe. Sur rendez-vous uniquement." },
              { icon: Calendar, title: "Devis personnalisé", desc: "Pour des volumes importants ou des questions spécifiques, contactez-nous pour un devis adapté à vos besoins." },
            ].map((info, i) => (
              <div key={i} className="p-6 rounded-xl flex gap-4" style={{ backgroundColor: "white", border: "1px solid #d8d1c4" }}>
                <info.icon size={24} style={{ color: "#2d5016", flexShrink: 0 }} />
                <div>
                  <h3 className="font-body font-600 text-sm mb-1" style={{ color: "#3a2b24" }}>{info.title}</h3>
                  <p className="text-xs leading-relaxed" style={{ color: "#3a2b24", opacity: 0.7, fontFamily: "'Satoshi', sans-serif" }}>{info.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ backgroundColor: "#2d5016" }} className="py-12 px-4 text-center">
        <h2 className="font-display text-3xl mb-4" style={{ color: "#f5f2ec" }}>Besoin d'un devis personnalisé ?</h2>
        <p className="text-sm mb-6 opacity-75" style={{ color: "#eae4d8", fontFamily: "'Satoshi', sans-serif" }}>
          Contactez-nous ou utilisez notre calculateur de surface pour estimer votre besoin.
        </p>
        <Link to="/contact" className="btn-secondary inline-flex items-center gap-2 px-8 py-3.5 rounded-md font-body font-500 text-base" data-testid="produits-cta">
          Nous contacter <ArrowRight size={18} />
        </Link>
      </section>
    </main>
  );
}
